import { useRef, type JSX, type ReactNode } from 'react';
import { term } from '../core/index.ts';
import type { BoardApi } from './useBoard.ts';

interface TimelineBarProps {
  readonly api: BoardApi;
}

/** How long a press on ◂ Back / Next ▸ lasts before it counts as held — about a key's repeat delay. */
const HOLD_MS = 400;

interface StepButtonProps {
  readonly api: BoardApi;
  readonly direction: 1 | -1;
  readonly className: string;
  readonly title: string;
  readonly children: ReactNode;
}

/**
 * ◂ Back / Next ▸, behaving exactly like Shift+Space / Space: a press steps a
 * phase (quick presses shuffle), and pressing and holding plays on smoothly
 * until let go — so a tablet without a keyboard gets the same controls.
 */
export function StepButton({ api, direction, className, title, children }: StepButtonProps): JSX.Element {
  const timer = useRef<number | null>(null);
  const holding = useRef(false);

  const letGo = (): void => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    if (!holding.current) return;
    holding.current = false;
    api.releasePhase();
  };

  return (
    <button
      type="button"
      className={className}
      title={title}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        api.tapPhase(direction);
        timer.current = window.setTimeout(() => {
          timer.current = null;
          holding.current = true;
          api.holdPhase(direction);
        }, HOLD_MS);
      }}
      onPointerUp={letGo}
      onPointerLeave={letGo}
      onPointerCancel={letGo}
      // A pointer press is handled above; this is Enter on a focused button.
      onClick={(event) => {
        if (event.detail === 0) api.tapPhase(direction);
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {children}
    </button>
  );
}

export function TimelineBar({ api }: TimelineBarProps): JSX.Element {
  const phaseWord = term(api.pack, 'play.keyframe', 'Phase');
  const activeFrame = api.doc.keyframes.find((k) => k.id === api.activeKeyframeId);

  return (
    <footer className="timeline">
      <div className="timeline__transport" data-tutorial="stepping">
        {/* The coach moves the play on when they are ready: a press steps a
            phase, holding plays on until let go. */}
        <StepButton
          api={api}
          direction={-1}
          className="btn"
          title={`Previous ${phaseWord.toLowerCase()} (Shift+Space) — hold to play backwards`}
        >
          ◂ Back
        </StepButton>
        <StepButton
          api={api}
          direction={1}
          className="btn btn--primary"
          title={`Next ${phaseWord.toLowerCase()} (Space) — hold to play on`}
        >
          Next ▸
        </StepButton>
        <button type="button" className="btn btn--ghost" onClick={() => api.seek(0)} title="Back to the start">
          ⏮
        </button>
        <span className="timeline__hint">
          <kbd>Space</kbd> next · <kbd>Shift</kbd>+<kbd>Space</kbd> back · hold to play
        </span>
      </div>

      <Scrubber api={api} />

      <div className="timeline__phases" data-tutorial="phases">
        <PhaseChips api={api} />
      </div>

      {activeFrame && (
        <div className="timeline__detail">
          <DeletePhaseButton api={api} />
        </div>
      )}
    </footer>
  );
}

/** One chip per phase, the active one lit, then + Phase. */
export function PhaseChips({ api }: TimelineBarProps): JSX.Element {
  const phaseWord = term(api.pack, 'play.keyframe', 'Phase');
  return (
    <>
      {api.doc.keyframes.map((frame, index) => {
        const drawn = api.doc.shapes.some((s) => s.keyframeId === frame.id);
        return (
          <button
            key={frame.id}
            type="button"
            className={`phase ${frame.id === api.activeKeyframeId ? 'phase--active' : ''}`}
            onClick={() => api.selectKeyframe(frame.id)}
            title={`${phaseWord} ${index + 1}${drawn ? ' · has drawings' : ''}`}
          >
            <span className="phase__index">{index + 1}</span>
            {drawn && <span className="phase__dot" aria-hidden="true" />}
          </button>
        );
      })}
      <button
        type="button"
        className="btn btn--ghost phase-add"
        onClick={api.addPhase}
        title={`Add ${phaseWord} after this one (K)`}
      >
        + {phaseWord}
      </button>
    </>
  );
}

export function DeletePhaseButton({ api }: TimelineBarProps): JSX.Element {
  const phaseWord = term(api.pack, 'play.keyframe', 'Phase');
  const activeFrame = api.doc.keyframes.find((k) => k.id === api.activeKeyframeId);
  return (
    <button
      type="button"
      className="btn btn--ghost btn--danger"
      onClick={() => activeFrame && api.deletePhase(activeFrame.id)}
      disabled={!activeFrame || api.doc.keyframes.length <= 1}
    >
      Delete {phaseWord.toLowerCase()}
    </button>
  );
}

/** Drags the playhead anywhere in the play. */
export function Scrubber({ api }: TimelineBarProps): JSX.Element {
  return (
    <input
      className="timeline__scrub"
      type="range"
      min={0}
      max={Math.max(1, api.durationMs)}
      // Whole milliseconds: phase starts are derived and rarely land on a
      // coarser step, which would leave the last phase out of reach.
      step={1}
      value={api.playhead}
      onChange={(event) => api.seek(Number(event.target.value))}
      aria-label="Scrub playhead"
    />
  );
}
